import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { NavIcon } from "./navIcons";
import {
  ClientsGlyph,
  EnquiryGlyph,
  HomeGlyph,
  TaskGlyph,
} from "./tabGlyphs";
import { useActivePath } from "./activePath";
import { useNavigationGuard } from "./UnsavedChanges";
import { ROUTES } from "../../routes/paths";
import { useNewEnquiryCount } from "../../features/enquiries/hooks/useNewEnquiryCount";
import { useNewClientCount } from "../../features/clients/hooks/useNewClientCount";

interface Tab {
  path: string;
  /**
   * What the tab is called, which is not always what the rail calls the same
   * destination: a bar three across on a 360px screen has room for one short
   * word each, so "My clients" is "Clients" and "Enquiries" is "Enquiry".
   *
   * The first tab is the exception that is not about width. The rail's
   * "Dashboard" is this bar's "Home", because that is the name the tab in
   * this position carries on a phone — the one you press to get back out of
   * wherever you are, rather than a section among the sections. Same route,
   * same screen; a different word for it down here.
   */
  label: string;
  Icon: NavIcon;
}

/**
 * The three places a phone goes between appointments, in the order the design
 * puts them. My task is the fourth and does not sit in the pill — see
 * `MY_TASK`.
 */
const TABS: Tab[] = [
  { path: ROUTES.dashboard, label: "Home", Icon: HomeGlyph },
  { path: ROUTES.myClients, label: "Clients", Icon: ClientsGlyph },
  { path: ROUTES.enquiries, label: "Enquiry", Icon: EnquiryGlyph },
];

/**
 * The one destination that stands outside the bar, in a circle of its own.
 *
 * Not a fourth tab demoted. The three in the pill are *lists* — a roster, an
 * inbox, a dashboard over both — and somebody goes to one of them to look
 * something up. My task is the day itself, which is the one screen a
 * practitioner opens because of where they are standing in the hour rather
 * than because of a record they want to read. Standing it apart, in the
 * brand's fill rather than the pill's white, is how the bar says so without a
 * word of explanation.
 *
 * Revenue held this circle until My task had a phone layout to show. It is on
 * the account menu now, at the head of it — see `MobileAccountMenu`. The swap
 * is the right way round: a week's takings are read once, at the end of the
 * week, and the day is read all day.
 *
 * There is no room for a label under a circle this size, so the name is
 * carried for a screen reader alone.
 */
const MY_TASK: Tab = {
  path: ROUTES.myTask,
  label: "My task",
  Icon: TaskGlyph,
};

/**
 * Which tab the chip was last parked over, kept outside the component on
 * purpose: this bar does not survive every navigation, and the chip's job is
 * to look as though it does.
 *
 * Going to the dashboard tears the whole shell down and builds it again.
 * `OfferTrialFirst` renders `TrialCheck` on that one path and `Outlet`
 * everywhere else — see `routes/guards` — and swapping the component at that
 * position unmounts everything under it, `AppLayout` and this bar with it. The
 * billing answer it is gating on is cached, so nothing is seen to happen; the
 * bar is simply a new one, and a new element has no previous position to
 * travel from. So Clients to Enquiry slid and Enquiry to Dashboard did not,
 * which is the one transition in the bar that most needs to.
 *
 * Module state rather than a ref, because a ref is lost with the component
 * that held it, and that is exactly the case this is for. It is one small
 * number describing a decoration, which is the only kind of state that belongs
 * out here: nothing is read from it that a wrong answer could spoil, and the
 * worst a stale one can do is slide a chip from the wrong side once.
 *
 * `null` until a bar has been mounted at all — distinct from -1, which is a
 * bar that was on screen with no tab of its own current.
 */
let parked: number | null = null;

/**
 * The navigation across the foot of a phone's section screens, standing in for
 * the rail that a phone has no width to keep open.
 *
 * Only those screens: `AppLayout` draws this on the four section roots and
 * nowhere under them, so a client, a plan, Settings or a form has the whole
 * height of the window and the back arrow in its title row instead. See
 * `barShown` there for why. Which is also why every path this bar tests is one
 * it is standing on or one it is about to go to — nothing in here has to
 * decide what to mark on a screen it is not drawn on.
 *
 * Two pieces rather than one band: a white pill carrying the three everyday
 * sections, and the My task circle beside it. Both float on the page's
 * off-white with room around them — which is what puts them within a thumb's
 * arc instead of flat against the bottom corner of the screen, and what lets
 * the one destination that is not a section be told apart from the three that
 * are at a glance.
 *
 * The glyphs come from `tabGlyphs`, which is Hugeicons' stroke-rounded set —
 * the same drawings the rail's assets were exported from, so a destination is
 * known by the same mark whichever navigation the reader is using.
 *
 * Where the rail marks its current item quietly, this does not. The rail is a
 * column the eye reads down; a tab bar is furniture at the far edge of the
 * screen, glanced at sideways, and a mark soft enough for the rail disappears
 * down there. So the current tab is given a filled chip of its own inside the
 * pill — the brand's palest wash, with glyph and label both recoloured into
 * the link teal against grey everywhere else. Three cues, not one: a shape,
 * hue and weight. The teal is the link teal rather than the brand's own
 * `#00C2CB`, which is too pale on white to carry a state by itself — it clears
 * 3:1 against neither the white behind it nor the grey it has to be told apart
 * from.
 *
 * Recolouring costs nothing because those glyphs are stroked in
 * `currentColor`: the `text-*` on the span around one is the whole of it.
 *
 * This is a phone app's furniture rather than a row of links in the small
 * things: a 56px target to land a thumb in, a squeeze on press, and no browser
 * tap flash over the top of it.
 *
 * Links rather than buttons, for the reason the setup checklist gives: these
 * go to screens of the app, so the status bar says where they lead and a long
 * press offers to open one in a tab. The guard is what a plain press runs
 * through instead — a tab is a press away from anywhere in the app, and it is
 * as good a way to walk out of a half-filled form as the rail is.
 */
const MobileTabBar: React.FC = () => {
  const navigate = useNavigate();
  const activePath = useActivePath();
  const guardNavigation = useNavigationGuard();

  // The same two tallies the rail badges — enquiries nobody has opened, and
  // clients with something new about them — read from the same queries, so
  // the two navigations never disagree about what is waiting. Shown here as
  // a dot rather than a number: a tab has no room beside its glyph for a
  // digit, and on a bar the eye passes over the news is that there is
  // something, not how much. The count is still in the name for a screen
  // reader. See `useNewEnquiryCount` and `useNewClientCount`.
  const newClients = useNewClientCount();
  const newEnquiries = useNewEnquiryCount();
  const waiting: Partial<Record<string, number>> = {
    [ROUTES.myClients]: newClients,
    [ROUTES.enquiries]: newEnquiries,
  };

  // What every press in here runs through. The browser's own navigation is
  // given up so the guard can ask first — but only for the plain press. A
  // modified click is the reader asking for a new tab, and this screen is not
  // being left at all.
  const press = (path: string) => (event: React.MouseEvent) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    event.preventDefault();
    guardNavigation(() => navigate(path));
  };

  const isActive = (path: string) => activePath.startsWith(path);
  const taskActive = isActive(MY_TASK.path);

  // Which tab is current, or -1 on My task — the one screen this bar is drawn
  // on that is not in the pill, where the chip has nowhere honest to be.
  const activeIndex = TABS.findIndex((tab) => isActive(tab.path));

  // Where the chip is *drawn*, which is not the same thing for one frame.
  //
  // It starts wherever the last bar left it, so a bar that has just been
  // rebuilt begins at the tab the reader came from rather than at the one they
  // arrived on — and then moves, which is the whole animation. A transition
  // needs two positions and a frame between them; mounting straight onto the
  // answer gives it one position and nothing to do.
  //
  // The very first bar of the session has nothing behind it and opens on the
  // current tab, still. There is no journey to show on a cold open, and a chip
  // sliding in from the left on the first screen of the app would be motion
  // standing in for nothing.
  const [chipIndex, setChipIndex] = useState(() => parked ?? activeIndex);

  useEffect(() => {
    parked = activeIndex;
    if (chipIndex === activeIndex) return;

    // Next frame, not this one: both positions have to be painted for the
    // browser to interpolate between them, and a state change in the same
    // frame as the mount is just the final position arriving early.
    const frame = requestAnimationFrame(() => setChipIndex(activeIndex));
    return () => cancelAnimationFrame(frame);
  }, [activeIndex, chipIndex]);

  return (
    <nav
      aria-label="Sections"
      // Over the page rather than in the column with it, which is what the
      // glass below is for: a frosted pill is a lie unless the screen it sits
      // on actually passes beneath it. The element itself is a transparent
      // strip the width of the window, and the pill and the circle are what
      // is drawn on it.
      //
      // The page does not end up under the bar for it. It runs the full height
      // of the window and keeps its last stretch clear by padding instead —
      // `--tab-bar-room`, set by `AppLayout` and spent by `.page-panel` in
      // `index.css`, which is also where these two paddings are measured. One
      // set of numbers, so the room a list keeps and the height it is keeping
      // room for cannot drift apart.
      //
      // `px-page` rather than a gutter of its own, so the pill lines up with
      // the cards it floats over instead of standing a few pixels wider than
      // every one of them. It is the same token the screens spend — see
      // `spacing.page` in the Tailwind config — which is the point: a bar that
      // measured its own inset would drift the first time the page's changed.
      //
      // `z-10` is what keeps it the foot of the screen during a
      // pull-to-refresh. The box behind is a static block at rest, but a pull
      // puts a `transform` on it — see `PullFrame` — and a transformed element
      // paints in the positioned layer, above static siblings whatever the
      // order. A positioned bar with a z-index of its own outranks it, so the
      // screen slides *under* the glass, which is the gesture as designed and
      // now the best look the bar gets.
      className="md:hidden absolute inset-x-0 bottom-0 z-10 select-none px-page pt-[var(--tab-bar-pad-top)] pb-[var(--tab-bar-pad-bottom)]"
    >
      <div className="flex items-center gap-3">
        {/* The everyday three, on frosted glass: the page keeps moving under
            the pill and arrives as colour without detail, so the bar reads as
            a layer over the app rather than as a strip cut out of it.

            Three things make the glass, and all three are needed. The blur is
            what the page becomes. The saturation boost is what stops it
            becoming grey — blurring a page averages its colours towards the
            middle, and `saturate` puts back what the averaging took. And the
            fill is what the white text and glyphs stand on: at 72% the brand
            teal still clears its contrast against it over a dark card passing
            underneath, which plain `white/50` does not.

            The hairline is in white rather than the grey it was: on glass the
            edge is a highlight catching the light, not a border drawn around
            a box. The shadow stays — the one thing glass cannot do for itself
            is say how far off the page it is floating.

            Falls back to solid white where there is no `backdrop-filter`.
            Without the guard the fill alone would apply, and a 72% white pill
            over an unblurred list is the list showing through it, legibly,
            which is worse than no glass at all. */}
        <ul className="relative flex min-w-0 flex-1 items-stretch rounded-full border border-white/70 bg-white shadow-[0_6px_24px_rgba(0,66,75,0.12)] supports-[backdrop-filter]:bg-white/[0.72] supports-[backdrop-filter]:backdrop-blur-xl supports-[backdrop-filter]:backdrop-saturate-150 p-1.5">
          {/* The mark on the current tab: one chip that slides, rather than a
              fill on each tab that fades in and out.

              This is the difference between a web page's tab bar and a phone
              app's. Three tabs with their own fills can only cross-dissolve,
              and a dissolve says two things changed; a single chip travelling
              from the tab you left to the tab you pressed says the selection
              moved, which is what actually happened. It is the one gesture on
              this bar worth animating at all.

              `cubic-bezier(0.34, 1.56, 0.64, 1)` is the overshoot: the chip
              arrives slightly past the tab and settles back onto it, the way a
              sprung control does. A plain ease-out covers the same ground and
              lands dead, and the difference is most of the feel.

              The arithmetic needs no measuring, which is why no layout library
              is wanted for it: the three tabs are `flex-1` inside a padded
              pill, so a chip one third of the pill's inner width moves exactly
              one of its own widths per tab, and `translateX` in percent is a
              percentage of the element itself. The step is the index.

              Hidden rather than parked somewhere arbitrary while My task is
              the current screen, and still transitioned while hidden, so
              coming back to a tab fades the chip in where it belongs instead
              of flying it in from wherever it was last. */}
          <span
            aria-hidden="true"
            className={`pointer-events-none absolute left-1.5 top-1.5 bottom-1.5 w-[calc((100%_-_0.75rem)/3)] rounded-full bg-[#E6FAFB] transition-[transform,opacity] duration-[420ms] ease-[cubic-bezier(0.34,1.56,0.64,1)] motion-reduce:transition-none ${
              activeIndex < 0 ? "opacity-0" : "opacity-100"
            }`}
            style={{
              transform: `translateX(${Math.max(chipIndex, 0) * 100}%)`,
            }}
          />
          {TABS.map((tab) => {
            const active = isActive(tab.path);
            const count = waiting[tab.path] ?? 0;
            return (
              // `relative` is what keeps the tab above the chip. Both are
              // positioned at the same level, so the later one in the markup
              // wins — and left static the tab would lose outright, since a
              // positioned element paints over in-flow content whatever the
              // order.
              <li key={tab.path} className="relative min-w-0 flex-1">
                <Link
                  to={tab.path}
                  aria-current={active ? "page" : undefined}
                  onClick={press(tab.path)}
                  // The target is the full width of the tab and a full 56px of
                  // it, not the glyph and label inside — that is what a thumb
                  // arriving at the bottom of the screen actually hits.
                  //
                  // `touch-manipulation` drops the 300ms the browser otherwise
                  // waits to see whether a tap was the first of a double-tap
                  // zoom, and the tap highlight is turned off because the
                  // squeeze below is the feedback; the grey flash over the top
                  // of it is what makes a bar read as a web page's.
                  //
                  // The press is the whole tab shrinking under the finger —
                  // glyph, label and all — rather than the glyph alone. That is
                  // the part a phone gets right and a web page usually does
                  // not: the thing you touched is the thing that moves, and it
                  // moves while you are still touching it rather than once you
                  // have let go.
                  //
                  // Two curves, which is what makes it read as sprung rather
                  // than merely animated. Going down is fast and flat — 100ms,
                  // ease-out — because a control that takes its time yielding
                  // feels slow under the thumb. Coming back up is the base
                  // transition: slower, and over the same overshoot the chip
                  // travels on, so the tab rebounds a touch past its size and
                  // settles. Press and hold and it stays down; let go and it
                  // springs.
                  className="group relative flex min-h-[56px] flex-col items-center justify-center gap-1 rounded-full px-1 transition-transform duration-[320ms] ease-[cubic-bezier(0.34,1.56,0.64,1)] touch-manipulation [-webkit-tap-highlight-color:transparent] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00C2CB]/40 motion-safe:active:scale-[0.93] motion-safe:active:duration-100 motion-safe:active:ease-out"
                >
                  {/* The glyph takes its colour from this span, which is all
                      `currentColor` on a stroke needs. */}
                  <span
                    className={`relative flex-shrink-0 transition-colors duration-200 ease-out ${
                      active ? "text-[#007E8C]" : "text-ink-muted"
                    }`}
                  >
                    <tab.Icon className="h-[22px] w-[22px]" />
                    {/* Off the glyph's top-right corner, in the rail badge's
                        red, with a hairline of the pill's own white around it
                        so it reads as resting on the glyph rather than as part
                        of it. Not greyed with an inactive tab: the news is the
                        one thing on a tab that should not fade. */}
                    {count > 0 && (
                      <span
                        aria-hidden="true"
                        className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-[#FF6158] ring-2 ring-white"
                      />
                    )}
                  </span>
                  <span
                    className={`max-w-full truncate text-[11px] leading-none transition-colors duration-200 ${
                      active
                        ? "font-bold text-[#007E8C]"
                        : "font-medium text-ink-muted"
                    }`}
                  >
                    {tab.label}
                  </span>
                  {count > 0 && (
                    <span className="sr-only">{`, ${count} new`}</span>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>

        {/* My task, apart. The same height as the pill beside it, so the two
            read as one row rather than as a button that happened to land
            there, and filled rather than outlined so the eye finds it first
            among the four marks.

            68px is that height, and it is the pill's arithmetic rather than a
            number that looked right: a 56px tab inside `p-1.5` is 56 + 6 + 6.
            At 56 the circle was the *tab's* height, which left it floating a
            few pixels inside the pill's top and bottom edges — near enough to
            match to look like a mistake rather than a choice. Outer edge to
            outer edge is what makes the two pieces one row.

            Its current state is a deepening rather than a wash — there is no
            white left under it to tint — plus a ring held off the fill by a
            band of the page's own colour, which is how a filled circle says
            "you are here" without changing shape. */}
        <Link
          to={MY_TASK.path}
          aria-current={taskActive ? "page" : undefined}
          onClick={press(MY_TASK.path)}
          // The same two curves as a tab, so one bar presses one way.
          className={`flex h-[68px] w-[68px] flex-shrink-0 items-center justify-center rounded-full text-white shadow-[0_6px_18px_rgba(0,126,140,0.32)] transition-[background-color,transform] duration-[320ms] ease-[cubic-bezier(0.34,1.56,0.64,1)] touch-manipulation [-webkit-tap-highlight-color:transparent] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00C2CB]/50 focus-visible:ring-offset-2 motion-safe:active:scale-[0.92] motion-safe:active:duration-100 motion-safe:active:ease-out ${
            taskActive
              ? "bg-[#007E8C] ring-2 ring-[#00C2CB]/50 ring-offset-2 ring-offset-[#FCFCFD]"
              : "bg-[#00C2CB]"
          }`}
        >
          {/* Grown with the circle, so the ratio between mark and fill is the
              one the smaller button had rather than a 24px glyph adrift in
              it. */}
          <MY_TASK.Icon className="h-7 w-7" />
          <span className="sr-only">{MY_TASK.label}</span>
        </Link>
      </div>
    </nav>
  );
};

/**
 * Test seam. `parked` outlives any one bar — that is the whole point of it —
 * so a suite that draws two unrelated bars has to say where one session of
 * them ends. Not called by the app.
 */
export const resetTabChipForTests = (): void => {
  parked = null;
};

export default MobileTabBar;
